import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Tooltip from 'rc-tooltip';
import {
    getTimesheets,
    getAdminTimesheets,
    findTimesheets,
    getTimesheetShifts,
    getApprovedTimesheets,
    getPendingTimesheets,
    approveTimesheet
} from '../../../../../../reducers/timekeeping';
import { FormattedMessage } from 'react-intl';
import messages from '../../../../../../text/messages';
import Table from '../../../../../../components/table';
import TimesheetActionsDialog from './../../../../dialogs/timesheetActions';
import { TIMESHEET_STATUS } from '../../../../../../text/enumerations';
import { getViceVersaFullName } from '../../../../../../utilities/string';
import { getClients } from '../../../../../../reducers/clients';
import { exportPayroll, exportExecupayPayroll } from '../../../../exports/payroll';
import SearchBar from '../../../../../../components/searchBar';
import { nameSort, clientSort, periodSort } from '../../_common';
import { sortNamePeriodAsc, sortNamePeriodDsc, sortMinutes, renderHours } from '../../_common';
import { abbrDateYear } from '../../../../../../utilities/dateTime';

class AdminTimesheetsList extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            searchString: ''
        };

        this.getColumnDefinition = this.getColumnDefinition.bind(this);
        this.renderButtons = this.renderButtons.bind(this);
        this.loadTimesheets = this.loadTimesheets.bind(this);
        this.filterResults = this.filterResults.bind(this);
    }

    loadTimesheets() {
        const { role, getAdminTimesheets, filter } = this.props;

        let statusFilterObject = {};
        let statusFilterCallback = undefined;
        let status = undefined;
        if (filter) {
            if (typeof filter === 'string') {
                statusFilterObject = { status: filter };
                statusFilterCallback = r => filter === r.status;
                status = filter;
            } else if (Array.isArray(filter) && filter.length > 0) {
                statusFilterObject = { status: filter };
                status = filter[0];
                statusFilterCallback = r => filter.includes(r.status);
            } else {
                status = undefined;
            }
        }
        getAdminTimesheets(role.id, statusFilterObject, status, undefined, statusFilterCallback);
    }

    componentDidMount() {
        const { role, clients, getClients } = this.props;
        if (!clients) {
            getClients(role.id);
        }

        this.loadTimesheets();
    }

    filterResults() {
        const { timesheets } = this.props;
        const { searchString } = this.state;

        if (!searchString.trim()) {
            return timesheets;
        }

        let filter = searchString.toLowerCase();
        return timesheets.filter(
            e =>
                e.job.user.email.toLowerCase().includes(filter) ||
                getViceVersaFullName(e.job.user)
                    .toLowerCase()
                    .includes(filter) ||
                e.job.hm.email.toLowerCase().includes(filter) ||
                getViceVersaFullName(e.job.hm)
                    .toLowerCase()
                    .includes(filter)
        );
    }

    getColumnDefinition() {
        const { approveTimesheet, getTimesheetShifts, clientsMap, stringFilter } = this.props;

        const renderColumn = timesheet => dom => (
            <TimesheetActionsDialog
                key={timesheet.id}
                job={timesheet.job}
                timesheet={timesheet}
                component={dom}
                reloadData={() => getTimesheetShifts(timesheet.id)}
            />
        );

        return [
            {
                label: <FormattedMessage {...messages.periodEnding} />,
                valueMap: d =>
                    renderColumn(d)(action => {
                        let isConfirmed =
                            d.worker_confirmed_at &&
                            moment.tz(d.worker_confirmed_at, d.job.job_timezone) >= moment.tz(d.last_shift_updated_at, d.job.job_timezone);

                        return (
                            <div className="row name-column clickable" onClick={action}>
                                <Tooltip
                                    trigger={'hover'}
                                    overlay={<FormattedMessage {...(isConfirmed ? messages.confirmed : messages.notConfirmed)} />}
                                    overlayClassName={'help-button-tooltip'}
                                >
                                    <div className={`col-xs-1 ${isConfirmed ? 'confirmed-timesheet' : 'not-confirmed-timesheet'}`}>
                                        <i className="material-icons">{isConfirmed ? 'check' : 'close'}</i>
                                    </div>
                                </Tooltip>
                                <div className="period-cell">{abbrDateYear(moment.tz(d.period_ending || d.created_at, d.job.work_timezone))}</div>
                            </div>
                        );
                    }),
                size: 'col-sm-2 hidden-xs',
                sort: (a, b) => periodSort(a, b) || clientSort(a, b, clientsMap) || nameSort(a, b),
                descSort: (a, b) => periodSort(b, a) || clientSort(a, b, clientsMap) || nameSort(a, b)
            },
            {
                label: <FormattedMessage {...messages.client} />,
                valueMap: d =>
                    renderColumn(d)(action => (
                        <div className="row name-column clickable" onClick={action}>
                            <div className="col-xs-12 name-cell ellipsis">
                                {d.job && d.job.client_id ? clientsMap[d.job.client_id] : <FormattedMessage {...messages.notProvided} />}
                            </div>
                        </div>
                    )),
                size: 'col-sm-3 hidden-xs',
                sort: (a, b) => clientSort(a, b, clientsMap) || periodSort(a, b) || nameSort(a, b),
                descSort: (a, b) => clientSort(b, a, clientsMap) || periodSort(a, b) || nameSort(a, b)
            },
            {
                label: <FormattedMessage {...messages.name} />,
                valueMap: d =>
                    renderColumn(d)(action => {
                        return (
                            <div className="row name-column clickable" onClick={action}>
                                <div className="col-xs-12">
                                    <div className="row">
                                        <div className="col-xs-12 name-cell ellipsis right-zero">{getViceVersaFullName(d.job.user)}</div>
                                        <div className="col-xs-12 email-cell ellipsis right-zero">{d.job.user.email}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    }),
                size: stringFilter === TIMESHEET_STATUS.SUBMITTED ? 'col-sm-3 h col-xs-8' : 'col-sm-4 col-xs-8',
                sort: sortNamePeriodAsc,
                descSort: sortNamePeriodDsc
            },
            {
                label: <FormattedMessage {...messages.hours} />,
                valueMap: d => renderColumn(d)(action => renderHours(action, d.minutes, d.expense_total, d.deliverable_total)),
                size: 'col-md-2 col-sm-2 col-xs-4 text-right',
                sort: sortMinutes
            },
            {
                valueMap: d => {
                    if ([TIMESHEET_STATUS.SUBMITTED, TIMESHEET_STATUS.RESUBMITTED].includes(d.status)) {
                        return (
                            <div className="full-height-cell">
                                <div className="approve-button" onClick={() => approveTimesheet(d.id, '', this.loadTimesheets)}>
                                    <FormattedMessage {...messages.approve} />
                                </div>
                            </div>
                        );
                    }
                },
                size: 'col-sm-1 hidden-xs text-right'
            }
        ];
    }

    isOrContains(stringOrArray, key) {
        if (typeof stringOrArray === 'string') return stringOrArray === key;
        if (Array.isArray(stringOrArray)) return stringOrArray.includes(key);
        return false;
    }

    renderButtons() {
        const { filter, timesheets, details, clients } = this.props;
        if (!this.isOrContains(filter, 'approved')) return null;

        return (
            <div className="col-xs-12 export-buttons">
                <div className="group">{exportPayroll(timesheets, clients)}</div>
                <div className="group">{exportExecupayPayroll(timesheets, details)}</div>
            </div>
        );
    }

    render() {
        const { timesheets, clients } = this.props;
        if (!timesheets || !clients) return null;

        if (timesheets.length === 0) {
            return (
                <div className="col-xs-12 timesheet-list">
                    <div className="empty-list">
                        <FormattedMessage {...messages.noTimesheetsFound} />
                    </div>
                </div>
            );
        }

        const filterResults = this.filterResults();

        return (
            <React.Fragment>
                {this.renderButtons()}
                <div className="col-sm-6 col-xs-12">
                    <SearchBar onSubmit={d => this.setState({ ...this.state, searchString: d.value })} />
                </div>
                <div className="col-xs-12">
                    <Table data={filterResults} columns={this.getColumnDefinition()} defaultSortColumn={0} />
                </div>
            </React.Fragment>
        );
    }
}

AdminTimesheetsList.defaultProps = {
    job: undefined,
    filter: 'all',
    workers: undefined
};
AdminTimesheetsList.defaultProps = {
    job: PropTypes.object,
    workers: PropTypes.array
};

const mapStateToProps = (state, props) => {
    const filter = Array.isArray(props.filter) && props.filter.length > 0 ? props.filter[0] : typeof props.filter === 'string' ? props.filter : 'all';
    const clientsMap = (state.clients.list || []).reduce((map, client) => {
        map[client.id] = client.name;
        return map;
    }, {});

    return {
        role: state.identity.role,
        details: state.identity.details,
        clients: state.clients.list,
        timesheets: state.timekeeping.timesheets[filter],
        shifts: state.timekeeping.timesheetShifts,
        stringFilter: filter,
        clientsMap
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getClients,
            approveTimesheet,
            findTimesheets,
            getTimesheets,
            getApprovedTimesheets,
            getPendingTimesheets,
            getAdminTimesheets,
            getTimesheetShifts
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(AdminTimesheetsList);



// WEBPACK FOOTER //
// ./src/pages/timekeeping/lists/timesheets/tenants/admin/index.jsx